import { isNil } from 'lodash';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { API } from '@shared/types/api';
import { usePersistentRef } from '@shared/util/hooks';
import { JsonStorage } from '@shared/util/storage';
import { ControlsState } from '../types';
import { useControlsActions, useControlsState } from './ControlsContext';
import { useViewTemplateState } from './ViewTemplateContext';
import {
  COLLECTION_CRITERIA_QUERY_PARAM,
  VIEW_TEMPLATE_ID_QUERY_PARAM,
  encodeCollectionCriteriaQueryParam,
  useSyncQueryParams,
} from './collectionViewQueryParamOverrides';
import { getRelatedEntityFilterComponentIds } from './getRelatedEntityFilterComponentIds';
import { hasUnresolvedRelatedFilters } from './hasUnresolvedRelatedFilters';
import { isCollectionCriteriaEqual } from './isCollectionCriteriaEqual';
import { mapCollectionCriteriaToState } from './mapCollectionCriteriaToState';
import { mapStateToCollectionCriteria } from './mapStateToCollectionCriteria';

// Backup only - the URL (kept in sync below) is the primary carrier of unsaved edits, since it
// can be copied/bookmarked/shared and survives independently of this device's storage. This TTL
// just covers the gap between an edit and this device's next visit/save/revert.
const COLLECTION_CRITERIA_TTL = 1000 * 60 * 60 * 24; // 1 day

interface Props {
  setIsModified: Dispatch<SetStateAction<boolean>>;
  resetState: (nextState: ControlsState) => void;
  /**
   * A collectionCriteria override read from the `collectionCriteria` query param at mount. When
   * present, it takes precedence over any localStorage override (see
   * `useCollectionCriteriaQueryParamOverride`).
   */
  queryParamCriteria: API.CollectionCriteriaFragment | null;
}

export function PreserveCollectionCriteria({
  setIsModified,
  resetState,
  queryParamCriteria,
}: Props) {
  const { savedViewTemplate, preserveEdits } = useViewTemplateState();
  const storageKey = `viewTemplate-${savedViewTemplate.id}-collectionCriteria`;
  const {
    filters,
    sorts,
    groups,
    availableFilters,
    availableSorts,
    availableGroups,
    availableRelatedFilters,
    availableRelatedGroups,
  } = useControlsState();
  const { requestRelatedFilterOptions } = useControlsActions();
  const syncQueryParams = useSyncQueryParams();

  // The localStorage backup is read into state rather than applied straight out of the effect
  // below: it can't be applied until the components it references have loaded, and re-reading it
  // on every dependency change would re-apply it over whatever the user has edited since.
  const [storedCriteria, setStoredCriteria] = useState<API.CollectionCriteriaFragment | null>(null);

  const hasReadStorageRef = useRef(false);

  // Read through a ref wherever a criteria is mapped, so that expanding a related card in a menu -
  // which registers demand for its nested options and so gives both maps a new identity - can't
  // re-run these effects and clobber in-progress edits. The `hasUnresolved*` flags below are the
  // related-options change that legitimately re-triggers them (see `hasUnresolvedRelatedFilters`).
  const relatedOptionsRef = usePersistentRef({ availableRelatedFilters, availableRelatedGroups });
  const hasUnresolvedSavedRelatedFilters = hasUnresolvedRelatedFilters(
    savedViewTemplate.collectionCriteria,
    availableRelatedFilters,
    availableRelatedGroups,
  );
  const hasUnresolvedStoredRelatedFilters = hasUnresolvedRelatedFilters(
    storedCriteria,
    availableRelatedFilters,
    availableRelatedGroups,
  );
  const hasUnresolvedQueryParamRelatedFilters = hasUnresolvedRelatedFilters(
    queryParamCriteria,
    availableRelatedFilters,
    availableRelatedGroups,
  );

  useEffect(() => {
    if (
      hasUnresolvedSavedRelatedFilters ||
      hasUnresolvedStoredRelatedFilters ||
      hasUnresolvedQueryParamRelatedFilters
    ) {
      // A cross-workflow filter's related workflow template is still loading, so reducer state is
      // knowably missing filters that one of the criteria feeding it contains. Comparing now would
      // report the view as modified, and persisting now would write the truncated criteria to the
      // URL and local storage. Wait for the next pass, once the nested options have resolved.
      return;
    }
    // Saved criteria can reference components that have since been archived or removed
    // from the workflow template. Those components can't be filtered on (the API rejects
    // them) so they never make it into the reducer state - meaning `updatedCriteria`
    // never includes them. Comparing against the raw saved criteria would therefore
    // always report the view as modified (and the revert button would appear to do
    // nothing). Normalize the saved criteria through the same mapping the state went
    // through so archived/removed components are ignored on both sides of the comparison.
    const savedCriteria = savedViewTemplate.collectionCriteria ?? null;
    const savedState = mapCollectionCriteriaToState(
      savedCriteria,
      availableFilters,
      availableSorts,
      availableGroups,
      relatedOptionsRef.current.availableRelatedFilters,
      relatedOptionsRef.current.availableRelatedGroups,
    );
    const comparableSavedCriteria = mapStateToCollectionCriteria(savedState);
    const updatedCriteria = mapStateToCollectionCriteria({ filters, sorts, groups });
    const isModified = !isCollectionCriteriaEqual(comparableSavedCriteria, updatedCriteria);
    setIsModified(isModified);

    // Only persist unsaved edits to local storage/the URL for usages that opt in (e.g. the
    // primary collection view page, and only while it's the plain route actually being viewed -
    // see `CollectionViewContainer`). Other usages should only ever reflect the filters passed in.
    if (!preserveEdits) {
      // Actively clear rather than just skipping - `preserveEdits` can flip false after these
      // params were already written (e.g. navigating to a subroute), and nothing else clears them.
      syncQueryParams({
        [VIEW_TEMPLATE_ID_QUERY_PARAM]: null,
        [COLLECTION_CRITERIA_QUERY_PARAM]: null,
      });
      return;
    }
    if (isModified) {
      // The view has unsaved filter/sort/group edits, so build a shareable link that captures
      // them: write `collectionCriteria` plus the `viewTemplateId` those edits belong to, together,
      // behind a single debounce (avoids a false positive while switching views). `viewTemplateId`
      // is only ever written alongside `collectionCriteria` - it exists purely to pin the edits to
      // the right view in a copied/bookmarked link. A saved view needs neither param; resolution
      // falls back to the path segment / cached slug, and writing `viewTemplateId` on its own would
      // become a live override (see `useViewTemplateIdQueryParamOverride`) that pins the wrong view.
      const timer = setTimeout(() => {
        JsonStorage.set(storageKey, updatedCriteria, COLLECTION_CRITERIA_TTL);
        syncQueryParams({
          [VIEW_TEMPLATE_ID_QUERY_PARAM]: savedViewTemplate.id,
          [COLLECTION_CRITERIA_QUERY_PARAM]: encodeCollectionCriteriaQueryParam(updatedCriteria),
        });
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      // The view matches what's saved - no unsaved edits to keep in storage or the URL.
      if (hasReadStorageRef.current) {
        JsonStorage.remove(storageKey);
      }
      syncQueryParams({
        [VIEW_TEMPLATE_ID_QUERY_PARAM]: null,
        [COLLECTION_CRITERIA_QUERY_PARAM]: null,
      });
    }
  }, [
    savedViewTemplate.id,
    savedViewTemplate.collectionCriteria,
    filters,
    sorts,
    groups,
    availableFilters,
    availableSorts,
    availableGroups,
    relatedOptionsRef,
    hasUnresolvedSavedRelatedFilters,
    hasUnresolvedStoredRelatedFilters,
    hasUnresolvedQueryParamRelatedFilters,
    setIsModified,
    storageKey,
    preserveEdits,
    syncQueryParams,
  ]);

  useEffect(() => {
    if (!preserveEdits || !isNil(queryParamCriteria)) {
      // A query param override (applied below) takes precedence over any localStorage override.
      return;
    }
    // load collection criteria from storage
    const savedCollectionCriteria = JsonStorage.get<API.CollectionCriteriaFragment>(storageKey);
    hasReadStorageRef.current = true;
    setStoredCriteria(savedCollectionCriteria ?? null);
  }, [storageKey, preserveEdits, queryParamCriteria]);

  useEffect(() => {
    if (!preserveEdits || !isNil(queryParamCriteria) || isNil(storedCriteria)) {
      return;
    }
    // This criteria is only ever read here, so the provider can't see it - register any
    // cross-workflow filters it carries so their related templates get fetched.
    requestRelatedFilterOptions(getRelatedEntityFilterComponentIds(storedCriteria));
    if (hasUnresolvedStoredRelatedFilters) {
      // Applying it now would drop those filters. The request above resolves them, which clears
      // this flag and brings us back here.
      return;
    }
    const nextState = mapCollectionCriteriaToState(
      storedCriteria,
      availableFilters,
      availableSorts,
      availableGroups,
      relatedOptionsRef.current.availableRelatedFilters,
      relatedOptionsRef.current.availableRelatedGroups,
    );
    resetState(nextState);
    setStoredCriteria(null);
  }, [
    storedCriteria,
    hasUnresolvedStoredRelatedFilters,
    availableFilters,
    availableSorts,
    availableGroups,
    relatedOptionsRef,
    requestRelatedFilterOptions,
    resetState,
    preserveEdits,
    queryParamCriteria,
  ]);

  useEffect(() => {
    if (!preserveEdits || isNil(queryParamCriteria)) {
      return;
    }
    if (hasUnresolvedQueryParamRelatedFilters) {
      // Don't rewrite the param from a state that's still missing its cross-workflow filters -
      // that would drop them from the very link that carried them.
      return;
    }
    const nextState = mapCollectionCriteriaToState(
      queryParamCriteria,
      availableFilters,
      availableSorts,
      availableGroups,
      relatedOptionsRef.current.availableRelatedFilters,
      relatedOptionsRef.current.availableRelatedGroups,
    );
    resetState(nextState);
    // Persist immediately (bypassing the debounce in the effect above), rather than waiting for
    // the general isModified/sync cycle to catch up - otherwise a fast refresh right after
    // opening the link could land before the debounced sync fires and lose the override. Unlike
    // before, the query param itself is left in the URL rather than stripped - it's only ever
    // read once at mount (see `useCollectionCriteriaQueryParamOverride`), and leaving it in place
    // means it stays a durable, shareable/bookmarkable record of the criteria being viewed.
    const normalizedCriteria = mapStateToCollectionCriteria(nextState);
    hasReadStorageRef.current = true;
    JsonStorage.set(storageKey, normalizedCriteria, COLLECTION_CRITERIA_TTL);
    syncQueryParams({
      [COLLECTION_CRITERIA_QUERY_PARAM]: encodeCollectionCriteriaQueryParam(normalizedCriteria),
    });
  }, [
    queryParamCriteria,
    availableFilters,
    availableSorts,
    availableGroups,
    relatedOptionsRef,
    hasUnresolvedQueryParamRelatedFilters,
    resetState,
    preserveEdits,
    storageKey,
    syncQueryParams,
  ]);

  return null;
}
